import { createContext, useContext, useMemo, useState, type ReactNode } from "react";
import type { AppLocale } from "../i18nConfig";
import { DEFAULT_LOCALE, resolveAppLocale } from "../i18nConfig";
import en from "../translations/en.json";
import de from "../translations/de.json";

type TranslationDictionary = Record<string, string>;
type TranslationParams = Record<string, string | number | boolean | null | undefined>;

interface I18nContextValue {
  locale: AppLocale;
  setLocale: (locale: string) => void;
  t: (message: string, params?: TranslationParams) => string;
}

const dictionaries: Record<AppLocale, TranslationDictionary> = {
  en,
  de,
};

const I18nContext = createContext<I18nContextValue | null>(null);

export function interpolateMessage(message: string, params?: TranslationParams) {
  if (!params) return message;
  return message.replace(/\{([^}]+)\}/g, (match, key) => {
    if (!(key in params)) return match;
    const value = params[key];
    return value == null ? "" : String(value);
  });
}

export function translateMessage(locale: AppLocale, message: string, params?: TranslationParams) {
  const template = dictionaries[locale][message] ?? dictionaries.en[message] ?? message;
  return interpolateMessage(template, params);
}

export function GTProvider(props: {
  children: ReactNode;
}) {
  const { children } = props;
  const [locale, setLocaleState] = useState<AppLocale>(DEFAULT_LOCALE);

  const value = useMemo<I18nContextValue>(() => ({
    locale,
    setLocale: (nextLocale: string) => {
      setLocaleState(resolveAppLocale(nextLocale));
    },
    t: (message: string, params?: TranslationParams) => translateMessage(locale, message, params),
  }), [locale]);

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
}

function useI18nContext() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("gt-react shim used outside GTProvider");
  }
  return context;
}

export function useGT() {
  return useI18nContext().t;
}

export function useLocale() {
  return useI18nContext().locale;
}

export function useSetLocale() {
  return useI18nContext().setLocale;
}

export function T(props: {
  children: ReactNode;
}) {
  return <>{props.children}</>;
}
